import { useEffect, useMemo, useState } from "react";
import { Chip, IconButton, Menu, Stack, Typography } from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Sync as ReapplyIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import { useConfirm } from "@accredo/fe-shared/contexts/ConfirmContext.jsx";
import { useDocumentTypeActions } from "../../../hooks/useDocumentTypeActions.jsx";
import { useDocumentTypePermissions } from "../../../hooks/permissions/useDocumentTypePermissions.jsx";
import { useDocumentCategoryMappingActions } from "../../../hooks/useDocumentCategoryMappingActions.jsx";
import { useDocumentCategoryMappingPermissions } from "../../../hooks/permissions/useDocumentCategoryMappingPermissions.jsx";
import DocumentTypeFormModal from "./DocumentTypeFormModal.jsx";
import DocumentTypeIconPicker from "./DocumentTypeIconPicker.jsx";
import { iconComponentForName } from "./documentTypeIconOptions.js";

/**
 * Document types settings table: rows are reordered by drag-and-drop
 * (DataTable's MRT row-ordering), persisting the new sort_order immediately
 * - not a raw sort_order field in the edit form. Icon selection is the same:
 * a dedicated picker, not a free-text icon name.
 */
export default function DocumentTypesTable() {
  const { enqueueSnackbar } = useSnackbar();
  const { documentTypes, isLoading, hasLoaded, load, create, update, remove, reorder } =
    useDocumentTypeActions();
  const { canCreate, canEdit, canDelete } = useDocumentTypePermissions();
  const {
    mappings,
    unmappedCategories,
    load: loadMappings,
    loadUnmapped,
    create: createMapping,
    remove: removeMapping,
    reapply,
  } = useDocumentCategoryMappingActions();
  const { canReapply } = useDocumentCategoryMappingPermissions();
  const confirm = useConfirm();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [iconMenuState, setIconMenuState] = useState({ anchorEl: null, rowId: null });

  useEffect(() => {
    load();
    loadMappings();
    loadUnmapped();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleReapply() {
    const confirmed = await confirm({
      title: "Zuordnungen anwenden",
      message:
        "Die aktuelle Zuordnung auf alle bestehenden Dokumente anwenden?",
      confirmLabel: "Anwenden",
    });
    if (!confirmed) return;
    try {
      const result = await reapply();
      enqueueSnackbar(`${result?.data?.updated ?? 0} Dokumente aktualisiert`, {
        variant: "success",
      });
    } catch {
      enqueueSnackbar("Zuordnung konnte nicht angewendet werden", {
        variant: "error",
      });
    }
  }

  async function handleSyncCategories(document_type_id, { toAdd, toRemove }) {
    const mappingIdsToRemove = mappings
      .filter(
        (mapping) =>
          mapping.document_type_id === document_type_id &&
          toRemove.includes(mapping.category)
      )
      .map((mapping) => mapping.id);

    await Promise.all([
      ...toAdd.map((category) =>
        createMapping({ category, document_type_id })
      ),
      ...mappingIdsToRemove.map((mapping_id) => removeMapping(mapping_id)),
    ]);
  }

  function openCreate() {
    setEditing(null);
    setModalOpen(true);
  }
  function openEdit(row) {
    if (!canEdit()) return;
    setEditing(row);
    setModalOpen(true);
  }
  function handleModalClose() {
    setModalOpen(false);
  }

  async function handleDelete(e, row) {
    e.stopPropagation();
    const confirmed = await confirm({
      title: "Dokumenttyp löschen",
      message: `Dokumenttyp „${row.label}" wirklich löschen?`,
      confirmLabel: "Löschen",
      destructive: true,
    });
    if (!confirmed) return;
    try {
      await remove(row.id);
      enqueueSnackbar("Dokumenttyp gelöscht", { variant: "success" });
    } catch {
      enqueueSnackbar("Dokumenttyp konnte nicht gelöscht werden", { variant: "error" });
    }
  }

  async function handleRowDragEnd({ draggingRow, hoveredRow }) {
    if (!hoveredRow || !draggingRow || hoveredRow.index === draggingRow.index) {
      return;
    }

    const newOrder = [...documentTypes];
    const [moved] = newOrder.splice(draggingRow.index, 1);
    newOrder.splice(hoveredRow.index, 0, moved);

    try {
      await reorder(newOrder.map((documentType) => documentType.id));
    } catch {
      enqueueSnackbar("Reihenfolge konnte nicht gespeichert werden", { variant: "error" });
    }
  }

  function openIconMenu(e, rowId) {
    e.stopPropagation();
    setIconMenuState({ anchorEl: e.currentTarget, rowId });
  }
  function closeIconMenu() {
    setIconMenuState({ anchorEl: null, rowId: null });
  }

  async function handleIconSelect(row, iconName) {
    closeIconMenu();
    try {
      await update(row.id, { icon: iconName });
    } catch {
      enqueueSnackbar("Icon konnte nicht gespeichert werden", { variant: "error" });
    }
  }

  const columns = useMemo(
    () => [
      {
        id: "icon",
        header: "Icon",
        size: 70,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const Icon = iconComponentForName(row.original.icon);
          return (
            <>
              <IconButton
                aria-label="Icon ändern"
                size="small"
                onClick={(e) => openIconMenu(e, row.original.id)}
                disabled={!canEdit()}
              >
                {Icon && <Icon fontSize="small" />}
              </IconButton>
              <Menu
                anchorEl={iconMenuState.anchorEl}
                open={Boolean(iconMenuState.anchorEl) && iconMenuState.rowId === row.original.id}
                onClose={closeIconMenu}
              >
                <DocumentTypeIconPicker
                  value={row.original.icon}
                  onChange={(iconName) => handleIconSelect(row.original, iconName)}
                />
              </Menu>
            </>
          );
        },
      },
      {
        accessorKey: "label",
        header: "Bezeichnung",
      },
      {
        accessorKey: "slug",
        header: "Slug",
        Cell: ({ cell }) => (
          <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
            {cell.getValue()}
          </Typography>
        ),
      },
      {
        id: "categories",
        header: "Kategorien (KATEGORIE1)",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {mappings
              .filter((mapping) => mapping.document_type_id === row.original.id)
              .map((mapping) => (
                <Chip key={mapping.id} label={mapping.category} size="small" />
              ))}
          </Stack>
        ),
      },
      {
        accessorKey: "is_active",
        header: "Aktiv",
        size: 90,
        Cell: ({ cell }) => (
          <Chip
            size="small"
            label={cell.getValue() ? "Aktiv" : "Inaktiv"}
            color={cell.getValue() ? "success" : "default"}
          />
        ),
      },
      canDelete() && {
        id: "actions",
        header: "",
        size: 56,
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnHiding: false,
        Cell: ({ row }) => (
          <IconButton
            aria-label="Löschen"
            size="small"
            onClick={(e) => handleDelete(e, row.original)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        ),
      },
    ].filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [iconMenuState, mappings]
  );

  return (
    <>
      <DataTable
        columns={columns}
        data={documentTypes}
        isLoading={isLoading}
        hasLoaded={hasLoaded}
        manualSorting={false}
        manualFiltering={false}
        enableGlobalFilter={true}
        enableColumnFilters={false}
        onRowClick={openEdit}
        enableRowOrdering={canEdit()}
        muiRowDragHandleProps={({ table }) => ({
          onDragEnd: () => handleRowDragEnd(table.getState()),
        })}
        toolbarActions={
          [
            canCreate() && {
              type: "button",
              label: "Neu",
              icon: <AddIcon />,
              onClick: openCreate,
              variant: "primary",
            },
            canReapply() && {
              type: "button",
              label: "Zuordnungen anwenden",
              icon: <ReapplyIcon />,
              onClick: handleReapply,
              variant: "tertiary",
            },
          ].filter(Boolean)
        }
      />

      <DocumentTypeFormModal
        open={modalOpen}
        onClose={handleModalClose}
        documentType={editing}
        assignedCategories={mappings
          .filter((mapping) => mapping.document_type_id === editing?.id)
          .map((mapping) => mapping.category)}
        unmappedCategories={unmappedCategories}
        onCreate={create}
        onUpdate={update}
        onSyncCategories={handleSyncCategories}
      />
    </>
  );
}
